import React, { useState } from 'react';
import { Box, Container, Typography, Collapse, IconButton } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { ExpandMore } from '@mui/icons-material';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Section = styled(Box)(({ theme }) => ({
  padding: '120px 0',
  background: theme.palette.background.default,
  borderTop: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('md')]: {
    padding: '72px 0',
  },
}));

const Eyebrow = styled(Typography)(({ theme }) => ({
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: theme.palette.primary.main,
  marginBottom: '12px',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
}));

const SectionHeading = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
  fontWeight: 700,
  letterSpacing: '-0.02em',
  color: theme.palette.text.primary,
  marginBottom: '56px',
  maxWidth: '720px',
  [theme.breakpoints.down('md')]: {
    marginBottom: '36px',
  },
  '& span': { color: theme.palette.text.secondary },
}));

const Row = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '180px 1fr auto',
  gap: '32px',
  alignItems: 'baseline',
  padding: '24px 0',
  borderTop: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  transition: 'opacity 0.2s ease',
  '&:hover': {
    '& .role': { color: theme.palette.primary.main },
  },
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr auto',
    gap: '6px 12px',
    padding: '20px 0',
  },
}));

const Period = styled(Box)(({ theme }) => ({
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.8125rem',
  color: theme.palette.text.secondary,
  fontWeight: 500,
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('md')]: { gridColumn: '1 / -1' },
}));

const RoleCol = styled(Box)({});

const RoleTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: '4px',
  transition: 'color 0.2s ease',
}));

const Company = styled(Typography)(({ theme }) => ({
  fontSize: '0.95rem',
  color: theme.palette.text.secondary,
  '& .sep': {
    margin: '0 8px',
    opacity: 0.5,
  },
}));

const ExpandBtn = styled(IconButton)(({ expanded, theme }) => ({
  width: 32,
  height: 32,
  color: theme.palette.text.secondary,
  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'transform 0.2s ease',
}));

const Detail = styled(Box)(({ theme }) => ({
  paddingLeft: '212px',
  paddingTop: '8px',
  paddingBottom: '12px',
  animation: `${fadeIn} 0.25s ease-out`,
  [theme.breakpoints.down('md')]: {
    paddingLeft: 0,
  },
}));

const Bullet = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '12px',
  marginBottom: '10px',
  fontSize: '0.95rem',
  color: theme.palette.text.secondary,
  lineHeight: 1.7,
  '&::before': {
    content: '"·"',
    color: theme.palette.primary.main,
    flexShrink: 0,
    fontWeight: 500,
  },
}));

const TagRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px',
  marginTop: '14px',
});

const Tag = styled(Box)(({ theme }) => ({
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontSize: '0.75rem',
  padding: '3px 10px',
  borderRadius: '999px',
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
}));

const CurrentBadge = styled(Box)(({ theme }) => ({
  display: 'inline-block',
  marginLeft: '10px',
  padding: '2px 8px',
  borderRadius: '999px',
  fontSize: '0.7rem',
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  fontWeight: 500,
  color: '#22c55e',
  border: '1px solid rgba(34, 197, 94, 0.4)',
  backgroundColor: 'rgba(34, 197, 94, 0.08)',
}));

const Experience = () => {
  const [open, setOpen] = useState(0);

  const toggle = (i) => setOpen(open === i ? -1 : i);

  const items = [
    {
      period: 'Jan 2026 - present',
      role: 'Software Engineer',
      company: 'Pavewise Pro',
      location: 'Fargo, ND',
      current: true,
      bullets: [
        'Building and maintaining cloud-based web applications across React (TypeScript) and Ruby on Rails',
        'Designing and consuming APIs to support system integrations and data workflows',
        'Modeling, querying, and reporting on PostgreSQL data',
        'Working with stakeholders to translate fuzzy requirements into reliable system features',
        'Supporting automation and deployment workflows to keep things shipping',
      ],
      tags: ['React', 'TypeScript', 'Ruby on Rails', 'PostgreSQL', 'REST APIs'],
    },
    {
      period: 'Mar 2025 - Dec 2025',
      role: 'Full Stack Developer Intern',
      company: 'Pavewise Pro',
      location: 'Fargo, ND',
      bullets: [
        'Developed frontend components in React/TypeScript and supported Rails-backed services',
        'Wrote tests, debugged production issues, and contributed to improving code quality',
        'Helped evaluate deployment and pipeline strategies',
      ],
      tags: ['React', 'TypeScript', 'Ruby on Rails', 'Testing'],
    },
    {
      period: 'Nov 2024 - Dec 2025',
      role: 'Student Supervisor, IT Services',
      company: 'NDSU Instructional Design Center',
      location: 'Fargo, ND',
      bullets: [
        'Supervised and scheduled student staff providing Tier 1 and Tier 2 support',
        'Coordinated escalation with sysadmins and specialized IT teams',
        'Owned service requests in Freshservice with clean documentation and timely resolution',
        'Translated user pain into actionable technical requests and support workflows',
      ],
      tags: ['Leadership', 'Freshservice', 'Documentation', 'Process'],
    },
    {
      period: 'Aug 2023 - Oct 2024',
      role: 'Graduate Student Technology Assistant',
      company: 'NDSU Instructional Design Center',
      location: 'Fargo, ND',
      bullets: [
        'Supported LMS platforms and classroom technology for faculty, staff, and students',
        'Wrote training material, tutorials, and system documentation that reduced repeat tickets',
        'Led workshops and 1:1 training sessions to drive technology adoption',
      ],
      tags: ['LMS', 'Training', 'Documentation'],
    },
    {
      period: 'Feb 2023 - Aug 2023',
      role: 'Front-End Developer Intern',
      company: 'TIENDA Logistics',
      location: 'Nairobi, KE',
      bullets: [
        'Built responsive UIs in HTML, CSS, JavaScript, and React',
        'Optimized frontend performance and ensured cross-browser behavior',
      ],
      tags: ['React', 'JavaScript', 'CSS'],
    },
    {
      period: 'Dec 2020 - Jul 2022',
      role: 'IT Assistant',
      company: 'Solar Rays Energy Sacco',
      location: 'Nairobi, KE',
      bullets: [
        'End-user support for hardware, software, and network issues',
        'Server monitoring, system maintenance, and software deployments',
      ],
      tags: ['IT Support', 'Sysadmin'],
    },
  ];

  return (
    <Section id="experience">
      <Container maxWidth="lg">
        <Eyebrow>Experience</Eyebrow>
        <SectionHeading>
          Where I've been.{' '}
          <span>From IT support in Nairobi to building product in Fargo.</span>
        </SectionHeading>

        <Box>
          {items.map((item, i) => (
            <Box key={i}>
              <Row onClick={() => toggle(i)}>
                <Period>{item.period}</Period>
                <RoleCol>
                  <RoleTitle className="role">
                    {item.role}
                    {item.current && <CurrentBadge>● current</CurrentBadge>}
                  </RoleTitle>
                  <Company>
                    {item.company}
                    <span className="sep">·</span>
                    {item.location}
                  </Company>
                </RoleCol>
                <ExpandBtn
                  expanded={open === i ? 1 : 0}
                  size="small"
                  aria-label="toggle"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(i);
                  }}
                >
                  <ExpandMore fontSize="small" />
                </ExpandBtn>
              </Row>
              <Collapse in={open === i} timeout={200}>
                <Detail>
                  {item.bullets.map((b, bi) => (
                    <Bullet key={bi}>{b}</Bullet>
                  ))}
                  <TagRow>
                    {item.tags.map((t) => (
                      <Tag key={t}>{t}</Tag>
                    ))}
                  </TagRow>
                </Detail>
              </Collapse>
            </Box>
          ))}
          <Box sx={{ borderTop: 1, borderColor: 'divider' }} />
        </Box>
      </Container>
    </Section>
  );
};

export default Experience;
